import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import adyenLogo from '../../adyen-logo.svg';
import UserDropdown from "../UserDropdown/UserDropdown";
import {useAuth} from "../../utils/AuthContext";
import styles from './HeaderComponent.module.css';
import {logout} from "../../api/AuthApi";
import {PAYMENT_ADVICE_ENABLED, TRANSACTIONS_ENABLED} from "../../AppConst";
import {pingNoAuth} from "../../api/CheckConnectionApi";

type HeaderProps = {
    handleGoHome: () => void;
    handleViewPaymentAdvice: () => void;
    handleViewTransactions: () => void;
};

const HeaderComponent = React.forwardRef<HTMLElement, HeaderProps>(
    ({handleGoHome, handleViewPaymentAdvice, handleViewTransactions}, ref) => {
        const navigate = useNavigate();
        const location = useLocation();
        const {setLoggedIn, userInfo, loading, loggedIn} = useAuth();
        type ConnectionStatus = 'pending' | 'online' | 'offline';
        const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('pending');
        const hasPingedOnceRef = useRef(false);

        useEffect(() => {
            if (location.pathname === "/login" || loading) {
                setConnectionStatus('pending');
                return;
            }

            if (!loggedIn) {
                hasPingedOnceRef.current = false;
                setConnectionStatus('pending');
                return;
            }

            if (hasPingedOnceRef.current) return;
            hasPingedOnceRef.current = true;

            let cancelled = false;

            pingNoAuth()
                .then((isOk) => {
                    if (cancelled) return;
                    setConnectionStatus(isOk ? 'online' : 'offline');
                })
                .catch(() => {
                    if (!cancelled) setConnectionStatus('offline');
                });

            return () => {
                cancelled = true;
            };
        }, [location.pathname, loading, loggedIn]);

        const handleLogout = () => {
            logout()
                .then(() => {
                    hasPingedOnceRef.current = false;
                    setConnectionStatus('pending');
                    setLoggedIn(false);
                    navigate("/login");
                })
                .catch((error) => {
                    console.error("Logout failed:", error);
                    hasPingedOnceRef.current = false;
                    setConnectionStatus('pending');
                    navigate("/login");
                });
        };

        const handleChangePassword = () => {
            navigate("/set-password");
        };

        const handleChangeEmail = () => navigate("/change-email");

        return (
            <header className={styles.header} ref={ref}>
                <div className={styles.headerLeft}>
                    <img src={adyenLogo} alt="adyenLogo" className={styles.logo}/>
                    {location.pathname !== '/login' && !location.pathname.startsWith('/reset-password') && (
                        <div className={styles.tilesContainer}>
                            <button
                                className={`${styles.tile} ${location.pathname === '/' ? styles.selected : ''}`}
                                onClick={handleGoHome}
                                type="button"
                            >
                                Merchants
                            </button>
                            {TRANSACTIONS_ENABLED && (
                                <button
                                    className={`${styles.tile} ${location.pathname === '/transactions' ? styles.selected : ''}`}
                                    type="button"
                                    onClick={handleViewTransactions}
                                >
                                    Transactions
                                </button>
                            )}
                            {PAYMENT_ADVICE_ENABLED && (
                                <button
                                    className={`${styles.tile} ${location.pathname === '/payment-advice' ? styles.selected : ''}`}
                                    type="button"
                                    onClick={handleViewPaymentAdvice}
                                >
                                    Payment Advice
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {location.pathname !== '/login' &&
                    !location.pathname.startsWith('/reset-password') &&
                    location.pathname !== '/error' && (
                        <div className={styles.headerRight}>
                            <div className={styles.checkConnection}>
                                {connectionStatus === 'pending' && (
                                    <span
                                        className={`${styles.statusDot} ${styles.pending}`}
                                        title="Checking connection…"
                                    />
                                )}

                                {connectionStatus === 'online' && (
                                    <span
                                        className={`${styles.statusDot} ${styles.online}`}
                                        title="Connection is established successfully with the Adyen DPA Adapter"
                                    />
                                )}

                                {connectionStatus === 'offline' && (
                                    <span
                                        className={`${styles.statusDot} ${styles.offline}`}
                                        title="There is a problem with communication with the Adyen DPA Adapter"
                                    />
                                )}
                            </div>
                            <p className={styles.tenantId} title={userInfo.tenantId}>
                                <b>Tenant ID:</b> {userInfo.tenantId}
                            </p>
                            <UserDropdown
                                email={userInfo.email}
                                onLogout={handleLogout}
                                onChangePassword={handleChangePassword}
                                onChangeEmail={handleChangeEmail}
                            />
                        </div>
                    )}
            </header>
        );
    }
);

export default HeaderComponent;